import React, { useState, useEffect } from 'react';
import { Pen, PencilOff, FileText, Zap, Crown, AlertTriangle, CheckCircle, Bot, Newspaper, ArrowLeft, TrendingUp, Globe, Gamepad2, Coffee, Shield, Smartphone, X } from 'lucide-react';
import axios from "./AxiosInstance.jsx";

const Decret = ({
    actions,
    setGameState,
    gameState,
    setActions,
    setMessage,
    message,
    setLoading,
    setShowNewspaperPopup,
    setPreviousArticles,
    onClose,
    setGuerres,
    setChatStatus, // Fonction pour afficher les messages de progression
    setActiveView,
    setSelectedYear,
    isMulti = false,
    submitMultiTurn = null,
}) => {
    const [currentPhase, setCurrentPhase] = useState('signing'); // signing, summary, journal, complete
    const [selectedDecrees, setSelectedDecrees] = useState([]);
    const [signingDecree, setSigningDecree] = useState(null);
    const [signatureProgress, setSignatureProgress] = useState(0);
    const [decrees, setDecrees] = useState([]);
    const [showStamp, setShowStamp] = useState(false);
    const [showStampBan, setShowStampBan] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);
    const [barre, setBarre] = useState(false);
    const [numActions, setNumActions] = useState(0);
    const [selectedActions, setSelectedActions] = useState([]);
    const [validationStatus, setValidationStatus] = useState(""); // New state for validation status
    const [isProcessingNextStep, setIsProcessingNextStep] = useState(false); // Flag pour éviter les appels multiples
    const isValidating = React.useRef(false); // Ref au lieu de State pour une mise à jour synchrone

    const leaderName = gameState?.joueur?.leader || "Dirigeant";
    const countryCode = gameState?.joueur?.name || "XX";
    const nbActions = actions ? actions.length : 0;

    // Animation de la signature lettre par lettre
    useEffect(() => {
        if (isAnimating) {
            const interval = setInterval(() => {
                setSignatureProgress(prev => {
                    if (prev >= leaderName.length) {
                        clearInterval(interval);
                        setTimeout(() => setShowStamp(true), 200);
                        setTimeout(() => {
                            if (!isProcessingNextStep) {
                                setIsProcessingNextStep(true);
                                handleSignatureNextStep();
                            }
                        }, 1000);
                        return prev;
                    }
                    return prev + 1;
                });
            }, 60);
            return () => clearInterval(interval);
        }
    }, [isAnimating, leaderName, isProcessingNextStep]);

    useEffect(() => {
        if (barre) {
            const timeout = setTimeout(() => {
                if (!isProcessingNextStep) {
                    setIsProcessingNextStep(true);
                    handleSignatureNextStep();
                }
            }, 1000);
            return () => clearTimeout(timeout);
        }
    }, [barre, isProcessingNextStep]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Enter' && !isAnimating && !barre) {
                if (currentPhase === 'summary') {
                    handleValidateTurn();
                } else if (currentPhase === 'signing') {
                    startSignature();
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [signingDecree, isAnimating, barre, currentPhase]);

    const handleSignatureNextStep = () => {
        const nextIndex = selectedDecrees.findIndex(d => d.id === signingDecree.id) + 1;
        if (nextIndex < selectedDecrees.length) {
            setSigningDecree(selectedDecrees[nextIndex]);
            setSignatureProgress(0);
            setNumActions(nextIndex + 1);
            setIsAnimating(false);
            setShowStamp(false);
            setBarre(false);
            setShowStamp(false);
            setShowStampBan(false);
            setIsProcessingNextStep(false); // Réinitialiser le flag pour le prochain décret
        } else {
            // Directement valider le tour
            setIsAnimating(false);
            setShowStamp(false);
            handleValidateTurn();
        }
    };

    const startSignature = () => {
        const action = {
            action: signingDecree.title,
            description: signingDecree.description,
            cible: signingDecree.category
        };
        setSelectedActions(prev => [...prev, action]);
        setIsAnimating(true);
        setSignatureProgress(0);
        setShowStamp(false);
    };

    const resetSignature = () => {
        if (currentPhase === 'summary') {
            onClose();
        } else {
            setBarre(true);
            setShowStampBan(true);
        }
    };

    useEffect(() => {
        if (actions && actions.length > 0) {
            const mappedDecrees = actions.map((action, idx) => ({
                id: idx + 1,
                title: action.action,
                category: action.cible || '',
                icon: <Zap className="w-4 h-4 sm:w-6 sm:h-6" />,
                description: action.description,
                consequences: [],
                urgency: '',
                color: 'from-blue-500 to-purple-600',
            }));

            setDecrees(mappedDecrees);
            setSelectedDecrees(mappedDecrees);
            setSigningDecree(mappedDecrees[0]);
            setNumActions(1);
        }
    }, [actions]);

    const handleValidateTurn = async () => {
        // Empêcher les appels multiples
        if (isValidating.current) {
            console.log("⚠️ Validation déjà en cours (BLOCKED BY REF), appel ignoré");
            return;
        }

        // Mode multijoueur : soumettre via submitMultiTurn au lieu de /validate_turn
        if (isMulti && submitMultiTurn) {
            isValidating.current = true;
            setLoading(true);
            setValidationStatus("Soumission de vos actions...");

            try {
                await submitMultiTurn(selectedActions);
                setValidationStatus("Actions soumises ! En attente des autres joueurs...");
                setSelectedActions([]);
                setActions([]);
                setMessage("");
                if (typeof setChatStatus === 'function') {
                    setChatStatus("Actions soumises ! En attente des autres joueurs...");
                }
            } catch (error) {
                console.error("Erreur soumission multi", error);
                const errMsg = typeof error === 'string' ? error : "Erreur lors de la soumission.";
                if (typeof setChatStatus === 'function') {
                    setChatStatus(errMsg);
                }
                setValidationStatus(errMsg);
            } finally {
                setLoading(false);
                isValidating.current = false;
            }
            return;
        }

        isValidating.current = true;
        setLoading(true);
        setValidationStatus("Démarrage de la validation du tour...");

        const pollTurnStatus = async (taskId, apiUrl, token) => {
            try {
                const res = await fetch(`${apiUrl}/turn_status/${taskId}`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                const text = await res.text();

                let data;
                try {
                    data = JSON.parse(text);
                } catch (e) {
                    throw new Error("Réponse invalide du serveur (pas du JSON) :\n" + text);
                }

                if (data.status === 'done') {
                    setGameState(data.game_state);
                    setGuerres(data.guerres);

                    const articlesResponse = await axios.get("/articles");
                    setPreviousArticles(articlesResponse.data);
                    setShowNewspaperPopup(true);

                    // TRIGGER MOBILE JOURNAL NAVIGATION
                    if (setActiveView) {
                        setActiveView('news');
                    }
                    if (setSelectedYear && data.game_state && data.game_state.year) {
                        setSelectedYear(data.game_state.year);
                    }

                    setSelectedActions([]);
                    setActions([]);
                    setMessage("");
                    if (typeof setChatStatus === 'function') {
                        setChatStatus("Tour terminé ! +2 ans");
                    }
                    setValidationStatus("Validation terminée !");
                    setLoading(false);
                    isValidating.current = false; // Réinitialiser le flag
                    // Fermer la popup après un court délai pour voir le message de succès
                    setTimeout(() => onClose(), 500);
                } else if (data.status === 'error') {
                    if (typeof setChatStatus === 'function') {
                        setChatStatus("Erreur : " + data.error);
                    }
                    setValidationStatus("Erreur : " + data.error);
                    setLoading(false);
                    isValidating.current = false; // Réinitialiser le flag
                } else {
                    if (data.log && data.log.length > 0) {
                        const last = data.log.at(-1);
                        if (typeof setChatStatus === 'function') {
                            setChatStatus(last);
                        }
                        setValidationStatus(last);
                    }
                    setTimeout(() => pollTurnStatus(taskId, apiUrl, token), 2000);
                }
            } catch (error) {
                console.error("Erreur de suivi du tour :", error);
                if (typeof setChatStatus === 'function') {
                    setChatStatus("Erreur pendant le suivi du tour.");
                }
                setValidationStatus("Erreur pendant le suivi du tour.");
                setLoading(false);
                isValidating.current = false; // Réinitialiser le flag
            }
        };

        try {
            const token = localStorage.getItem('jwtToken');
            const apiUrl = process.env.REACT_APP_API_URL || 'http://127.0.0.1:5000';

            console.log("🚀 Envoi de la requête validate_turn...");
            const response = await fetch(`${apiUrl}/validate_turn`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ actions: selectedActions })
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            const taskId = data.task_id;
            if (!taskId) {
                throw new Error("Aucune tâche retournée.");
            }

            setValidationStatus("Tour en cours...");
            pollTurnStatus(taskId, apiUrl, token); // NE PAS arrêter loading ici
        } catch (error) {
            console.error("Erreur validation tour", error);
            setChatStatus("Erreur de communication avec le serveur.");
            setValidationStatus("Erreur de communication avec le serveur.");
            setLoading(false); // ❗ uniquement ici si échec immédiat
            isValidating.current = false; // Réinitialiser le flag
        }
    };

    // Bouton mobile optimisé
    const MobileButton = ({ text, onClick, variant = "blue", disabled = false, icon: Icon }) => (
        <button
            onClick={onClick}
            disabled={disabled}
            className={`
                w-full px-4 py-3 sm:px-6 sm:py-4 rounded-xl font-semibold text-sm sm:text-base
                transition-all duration-300 transform active:scale-95
                flex items-center justify-center gap-2 min-h-[50px]
                ${variant === 'green'
                    ? 'bg-gradient-to-r from-green-600 to-green-500 hover:from-green-700 hover:to-green-600 text-white shadow-lg shadow-green-500/30'
                    : variant === 'red'
                        ? 'bg-gradient-to-r from-red-600 to-red-500 hover:from-red-700 hover:to-red-600 text-white shadow-lg shadow-red-500/30'
                        : 'bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white shadow-lg shadow-blue-500/30'
                }
                ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-xl'}
            `}
        >
            {Icon && <Icon className="w-4 h-4 sm:w-5 sm:h-5" />}
            {text}
        </button>
    );

    return (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4">
            <div className="w-full max-w-4xl max-h-[95vh] bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-xl sm:rounded-2xl overflow-hidden shadow-2xl">

                {/* Header Mobile-First */}
                <div className="p-3 sm:p-4 border-b border-slate-700/50 bg-slate-900/90">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 sm:gap-3">
                            <div className="p-1.5 sm:p-2 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg">
                                <FileText className="w-4 h-4 sm:w-6 sm:h-6 text-white" />
                            </div>
                            <div>
                                <h1 className="text-sm sm:text-xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                                    Chambre des Décrets
                                </h1>
                                <p className="text-xs sm:text-sm text-slate-400">
                                    Session {gameState?.year}-{gameState?.year + 2}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
                        >
                            <X className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400" />
                        </button>
                    </div>
                </div>

                {/* Phase de signature - Mobile optimisé */}
                {currentPhase === 'signing' && signingDecree && (
                    <div className="flex-1 overflow-y-auto p-3 sm:p-6">
                        {/* Parchemin mobile-first */}
                        <div className="bg-gradient-to-b from-amber-50 to-amber-100 rounded-lg sm:rounded-xl p-4 sm:p-8 shadow-xl border-2 sm:border-4 border-amber-200 mb-4">

                            {/* En-tête */}
                            <div className="text-center mb-4 sm:mb-6">
                                <h2 className="text-lg sm:text-2xl font-bold text-slate-800 mb-2 font-serif">
                                    DÉCRET OFFICIEL
                                </h2>
                                <div className="w-16 sm:w-24 h-0.5 sm:h-1 bg-gradient-to-r from-blue-600 to-purple-600 mx-auto mb-2 sm:mb-3"></div>
                                <p className="text-xs sm:text-sm text-slate-600 font-medium">
                                    {countryCode} • Document #{numActions}/{nbActions}
                                </p>
                            </div>

                            {/* Contenu du décret */}
                            <div className="mb-4 sm:mb-6 text-slate-700 leading-relaxed">
                                <p className={`text-xs sm:text-sm font-serif mb-3 sm:mb-4 transition-all duration-500 ${barre ? 'line-through opacity-50' : ''}`}>
                                    Par la présente, {leaderName}, dirigeant de {gameState?.joueur?.short_name},
                                    ordonne l'application des mesures suivantes :
                                </p>

                                <div className={`bg-slate-100 p-3 sm:p-4 rounded-lg border-l-4 border-blue-500 mb-4 sm:mb-6 transition-all duration-500 ${barre ? 'line-through opacity-50' : ''}`}>
                                    <h3 className="font-semibold text-slate-800 text-sm sm:text-base mb-2">
                                        {signingDecree?.title}
                                    </h3>
                                    <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                                        {signingDecree?.description}
                                    </p>
                                    {signingDecree?.category && (
                                        <div className="mt-2 inline-block px-2 py-1 bg-blue-100 rounded-full text-xs text-blue-700">
                                            {signingDecree.category}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Zone de signature mobile */}
                            <div className="border-t-2 border-slate-300 pt-4 sm:pt-6">
                                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-end gap-4">
                                    {/* Signature */}
                                    <div className="flex-1">
                                        <p className="text-xs sm:text-sm text-slate-500 mb-2">Signature :</p>
                                        <div className="relative h-12 sm:h-16 border-b border-slate-300">
                                            <div
                                                className="absolute bottom-1 sm:bottom-2 left-0 text-xl sm:text-3xl font-bold text-black transition-all duration-300"
                                                style={{
                                                    fontFamily: 'Brush Script MT, cursive',
                                                    transform: 'rotate(-1deg)',
                                                    filter: 'drop-shadow(1px 1px 2px rgba(0,0,0,0.3))'
                                                }}
                                            >
                                                {leaderName.slice(0, signatureProgress)}
                                                {isAnimating && signatureProgress < leaderName.length && (
                                                    <span className="animate-pulse text-blue-600 text-base sm:text-2xl">|</span>
                                                )}
                                            </div>

                                            {isAnimating && (
                                                <div
                                                    className="absolute bottom-0 sm:bottom-1 w-1 h-1 bg-blue-800 rounded-full animate-pulse opacity-30"
                                                    style={{
                                                        left: `${Math.min(signatureProgress * 8, 150)}px`,
                                                    }}
                                                ></div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Tampons */}
                                    <div className="flex gap-2 sm:gap-4 justify-center sm:justify-end">
                                        {/* Tampon validé */}
                                        <div
                                            className={`w-16 h-16 sm:w-20 sm:h-20 rounded-full border-3 sm:border-4 border-green-600 flex items-center justify-center relative transition-all duration-500 ${showStamp ? 'opacity-100 scale-100 rotate-6' : 'opacity-0 scale-50'}`}
                                            style={{
                                                background: 'radial-gradient(circle, rgba(38,220,38,0.8) 0%, rgba(38,220,38,0.4) 100%)',
                                            }}
                                        >
                                            <div className="text-center">
                                                <div className="text-green-900 font-bold text-xs sm:text-sm">
                                                    {gameState?.joueur?.short_name}
                                                </div>
                                                <div className="text-green-800 font-black text-xs">VALIDÉ</div>
                                            </div>
                                            {showStamp && (
                                                <div className="absolute inset-0 rounded-full bg-green-600 opacity-20 animate-ping"></div>
                                            )}
                                        </div>

                                        {/* Tampon abandon */}
                                        <div
                                            className={`w-16 h-16 sm:w-20 sm:h-20 rounded-full border-3 sm:border-4 border-red-600 flex items-center justify-center relative transition-all duration-500 ${showStampBan ? 'opacity-100 scale-100 rotate-6' : 'opacity-0 scale-50'}`}
                                            style={{
                                                background: 'radial-gradient(circle, rgba(220,38,38,0.8) 0%, rgba(220,38,38,0.4) 100%)',
                                            }}
                                        >
                                            <div className="text-center">
                                                <div className="text-red-900 font-bold text-xs sm:text-sm">
                                                    {gameState?.joueur?.short_name}
                                                </div>
                                                <div className="text-red-800 font-black text-xs">ABANDON</div>
                                            </div>
                                            {showStampBan && (
                                                <div className="absolute inset-0 rounded-full bg-red-600 opacity-20 animate-ping"></div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Date */}
                                <div className="mt-3 sm:mt-4 text-right text-xs sm:text-sm text-slate-500">
                                    {new Date().toLocaleDateString('fr-FR')}
                                </div>
                            </div>
                        </div>

                        {/* Boutons d'action mobile */}
                        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                            <MobileButton
                                text={isAnimating ? 'Signature...' : 'Signer le décret'}
                                onClick={startSignature}
                                variant="green"
                                disabled={isAnimating || barre}
                                icon={Pen}
                            />
                            <MobileButton
                                text="Abandonner"
                                onClick={resetSignature}
                                variant="red"
                                disabled={isAnimating || barre}
                                icon={X}
                            />
                        </div>

                        {/* Barre de progression mobile */}
                        {isAnimating && (
                            <div className="mt-4 w-full bg-slate-700 rounded-full h-2 overflow-hidden">
                                <div
                                    className="h-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-300"
                                    style={{ width: `${(signatureProgress / leaderName.length) * 100}%` }}
                                ></div>
                            </div>
                        )}
                    </div>
                )}

                {/* Phase résumé - Mobile optimisé */}
                {currentPhase === 'summary' && (
                    <div className="flex-1 overflow-y-auto p-3 sm:p-6">
                        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-slate-700/50 mb-4">
                            <div className="flex items-center gap-3 mb-4 sm:mb-6">
                                <div className="p-2 sm:p-3 bg-gradient-to-r from-green-500 to-blue-500 rounded-lg">
                                    <Bot className="w-4 h-4 sm:w-6 sm:h-6" />
                                </div>
                                <div className="flex-1">
                                    <h2 className="text-base sm:text-xl font-bold text-white">IA Ministre</h2>
                                    <p className="text-slate-400 text-xs sm:text-sm">Analyse stratégique</p>
                                    {message && (
                                        <p className="text-sm sm:text-base font-bold text-blue-400 mt-1">{message}</p>
                                    )}
                                    {validationStatus && (
                                        <p className="text-sm sm:text-base font-bold text-blue-400 mt-1">{validationStatus}</p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Boutons finaux */}
                        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                            <MobileButton
                                text="VALIDER LE MANDAT"
                                onClick={handleValidateTurn}
                                variant="green"
                                icon={CheckCircle}
                                disabled={validationStatus !== "" && validationStatus !== "Validation terminée !"}
                            />
                            <MobileButton
                                text="RETOUR"
                                onClick={resetSignature}
                                variant="red"
                                icon={ArrowLeft}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Decret;
